import {
  ORDER_COMMENT_STATUSES,
  ORDER_COMMENT_STATUS_LABEL,
  isOrderCommentStatus,
  type OrderCommentStatus,
} from '@/models/OrderCommentStatus';
import './commentStatus.css';

/** One status as a chip: on a note, beside an order's comments, and on the Orders list (SSD-7609). */
export function CommentStatusChip({ status, title }: { status: OrderCommentStatus; title?: string }) {
  const { label, tone } = ORDER_COMMENT_STATUS_LABEL[status];
  return (
    <span className={`cmt-status-chip tone-${tone}`} title={title}>
      {label}
    </span>
  );
}

export interface CommentStatusPickerProps {
  value: OrderCommentStatus | '';
  onChange: (value: OrderCommentStatus | '') => void;
  disabled?: boolean;
}

/** The status a new note carries, picked beside its text; none is the default, so a plain note stays plain. */
export function CommentStatusPicker({ value, onChange, disabled = false }: CommentStatusPickerProps) {
  return (
    <select
      className="cmt-status-pick"
      aria-label="Comment status"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(isOrderCommentStatus(event.target.value) ? event.target.value : '')}
    >
      <option value="">No status</option>
      {ORDER_COMMENT_STATUSES.map((status) => (
        <option key={status} value={status}>
          {ORDER_COMMENT_STATUS_LABEL[status].label}
        </option>
      ))}
    </select>
  );
}

export interface CommentPinButtonProps {
  pinned: boolean;
  text: string;
  disabled?: boolean;
  onToggle: () => void;
}

/** Pins a note to the top of the thread, or takes the pin off; one pin per order, so pinning moves it (SSD-7609). */
export function CommentPinButton({ pinned, text, disabled = false, onToggle }: CommentPinButtonProps) {
  return (
    <button
      type="button"
      className={`cmt-pin${pinned ? ' on' : ''}`}
      aria-label={`${pinned ? 'Unpin' : 'Pin'} comment: ${text}`}
      aria-pressed={pinned}
      title={
        pinned
          ? 'Unpin: this note stops showing first'
          : 'Pin to the top of this order: the note read first at a hand-over'
      }
      disabled={disabled}
      onClick={onToggle}
    >
      {pinned ? 'Pinned' : 'Pin'}
    </button>
  );
}
